import { useNavigate } from 'react-router-dom'
import { Clipboard } from 'lucide-react'
import { PiTag, PiMegaphone } from 'react-icons/pi'
import { BsTruck } from 'react-icons/bs'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'

interface MobileAccountAttributesSummaryProps {
  chainProxyId: number
  chainName: string
  doorProxyId?: number
}

export function MobileAccountAttributesSummary({
  chainProxyId,
  chainName,
  doorProxyId,
}: MobileAccountAttributesSummaryProps) {
  const navigate = useNavigate()
  const { data: effectiveData } = useCompanyOrDoorData(
    chainProxyId,
    doorProxyId
  )

  const defaultGrey = MULTI_VALUE_SELECT_COLORS[0].background
  const gpoBackground = MULTI_VALUE_SELECT_COLORS[1].background // Yellow for GPOs

  const tagCount = effectiveData?.taglist?.length || 0
  const campaignCount = effectiveData?.campaigns?.length || 0
  const distributors = effectiveData?.distributors || []
  const gpos = effectiveData?.gpos || []

  const handleClick = () => {
    // Navigate to the account attributes page
    navigate('account-attributes', {
      state: { chainProxyId, chainName },
    })
  }

  // Check if there's anything to display
  const hasContent =
    tagCount > 0 ||
    campaignCount > 0 ||
    distributors.length > 0 ||
    gpos.length > 0

  return (
    <div
      onClick={handleClick}
      className="bg-gray-50 rounded-lg border border-gray-200 shadow-md p-4 cursor-pointer active:bg-gray-100 transition-colors"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Clipboard className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold tracking-wide uppercase">
          ACCOUNT ATTRIBUTES
        </span>
      </div>

      {/* Content */}
      {hasContent ? (
        <div className="flex flex-wrap gap-2">
          {/* Tags Badge */}
          {tagCount > 0 && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded-md">
              <PiTag size={14} className="text-gray-600" />
              <span className="text-xs font-medium text-gray-700">
                {tagCount} {tagCount === 1 ? 'tag' : 'tags'}
              </span>
            </div>
          )}

          {/* Campaigns Badge */}
          {campaignCount > 0 && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded-md">
              <PiMegaphone size={14} className="text-gray-600" />
              <span className="text-xs font-medium text-gray-700">
                {campaignCount} {campaignCount === 1 ? 'campaign' : 'campaigns'}
              </span>
            </div>
          )}

          {/* Distributor Pills */}
          {distributors.map((distributor) => (
            <div
              key={distributor.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200"
              style={{ backgroundColor: defaultGrey }}
            >
              <BsTruck size={14} className="text-gray-600" />
              <span className="text-xs font-medium text-gray-700">
                {distributor.name}
              </span>
            </div>
          ))}

          {/* GPO Pills */}
          {gpos.map((gpo) => (
            <div
              key={gpo.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-yellow-200"
              style={{ backgroundColor: gpoBackground }}
            >
              <span className="text-xs font-medium text-yellow-800">
                {gpo.name}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-400">No attributes assigned</div>
      )}
    </div>
  )
}
